// #4980: one value saying what happened to the single process restart this app may spend.
//
// The background — why Android has a process restart at all, and what #4980 wants to hang
// off it — is in `design-docs/boot-sequence.md` ("The rungs, in order") and
// `design-docs/telemetry.md`. The one thing that must be said HERE, because it constrains
// every future edit to this file: keep it free of every import. The unit test loads it
// under plain node, which cannot load `app-restart.ts` at all — that module reaches
// `environments/environment-helpers` and the whole Angular graph behind it. Same pattern,
// and the same reason, as `local-store-probe.ts`.

/** What the native side says about the one process restart this app may spend (#4980). */
export enum ProcessRestartState {
  /** There is no process to restart here, so the question does not apply — web, iOS native. */
  NotApplicable = 'not-applicable',
  /** A restart was already spent, and the boot failed again anyway. #4980's whole trigger. */
  Spent = 'spent',
  /** The budget is intact: no restart has been made for this. */
  Available = 'available',
  /** Android, but the native side could not answer. */
  Unknown = 'unknown',
}

/** What the native side answered, or null when the question could not be asked at all. */
export interface ProcessRestartAnswer {
  /** Has the app already spent its one restart? */
  used: boolean;
}

/**
 * Turn what the native side answered into the one fact a report needs (#4980).
 *
 * "I could not ask" is deliberately its own answer rather than being folded into either
 * real one. Counting an unanswerable question as `Spent` would invent exactly the
 * occurrences #4980 is waiting to see, and counting it as `Available` would hide them.
 */
export function readProcessRestartState(
  canRestart: boolean,
  answer: ProcessRestartAnswer | null,
): ProcessRestartState {
  // if this platform has no process to restart, the platform decides, not a stray answer
  if (!canRestart) return ProcessRestartState.NotApplicable;
  // if the native side said nothing usable, say so rather than guessing either way
  if (!answer || typeof answer.used !== 'boolean') return ProcessRestartState.Unknown;
  return answer.used ? ProcessRestartState.Spent : ProcessRestartState.Available;
}
